import React from 'react';
import type { Theme } from '@material-ui/core';
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
} from '@material-ui/core';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useTranslation } from 'react-i18next';
import { FieldArray, useFormikContext } from 'formik';
import { KostnadskalkyleRow } from './KostnadskalkyleRow';
import { useKostnadsKalkyleContext } from '../../context/KostnadsKalkyleProvider';
import type { KostnadsSkjemaKalkyleRequest } from '@klp/prosjekt/services/types';
import { ActionButton, Block } from '@klp/design/index';
import { IconName, Illustration } from '@klp/design/Illustrations';

const styles = (theme: Theme) => ({
    tableContainer: {
        marginBottom: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
    },
    cell: {
        '&.description': { width: '30%' },
        '&.unit': { width: '15%' },
        '&.pricePerUnit': { width: '15%' },
        '&.cost': { width: '15%' },
        '&.costShare': { width: '15%' },
        '&.actions': { width: '10%' },
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),
    },
});

const EMPTY_KOSTNADSLINJE = {
    antallEnhet: 0,
    prisPrEnhet: 0,
    andelAktivering: 0,
    andelKostnadsforing: 0,
    kostnadstype: null,
} as const;

export const KostnadsKalkyleFormTable = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { isReadOnly, setSelectedTab } = useKostnadsKalkyleContext();
    const { values, handleChange } =
        useFormikContext<KostnadsSkjemaKalkyleRequest>();

    if (!values) return null;

    return (
        <Table className={classes.tableContainer}>
            <TableHead>
                <TableRow>
                    <TableCell className={`${classes.cell} description`}>
                        <div className={classes.headerContent}>
                            {t('prosjekt.kostnadskalkyle.table.kostnadstype')}
                            <IconButton
                                size="small"
                                onClick={() => setSelectedTab('kostnadstyper')}
                                title={t(
                                    'prosjekt.kostnadskalkyle.administrerKostnadstyper',
                                )}
                            >
                                <Illustration
                                    icon={IconName.EDIT}
                                    size="small"
                                    klpPalette="secondary"
                                />
                            </IconButton>
                        </div>
                    </TableCell>
                    <TableCell className={`${classes.cell} unit`}>
                        {t('prosjekt.kostnadskalkyle.table.enhet')}
                    </TableCell>
                    <TableCell className={`${classes.cell} pricePerUnit`}>
                        {t('prosjekt.kostnadskalkyle.table.prisPrEnhet')}
                    </TableCell>
                    <TableCell className={`${classes.cell} cost`}>
                        {t('prosjekt.kostnadskalkyle.table.kostnad')}
                    </TableCell>
                    <TableCell className={`${classes.cell} costShare`}>
                        {t(
                            'prosjekt.kostnadskalkyle.table.kostnadsforingAndel',
                        )}
                    </TableCell>
                    <TableCell
                        className={`${classes.cell} actions`}
                        aria-label="Handlinger"
                    />
                </TableRow>
            </TableHead>
            <FieldArray name="kostnadslinjer">
                {(arrayHelpers) => {
                    return (
                        <>
                            <TableBody>
                                {values.kostnadslinjer?.map((row, index) => (
                                    <KostnadskalkyleRow
                                        key={`kostnadslinjer.${index}`}
                                        row={row}
                                        index={index}
                                        isReadOnly={isReadOnly}
                                        handleChange={handleChange}
                                        remove={() => {
                                            arrayHelpers.remove(index);
                                        }}
                                    />
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Block className={classes.footer}>
                                            {!isReadOnly && (
                                                <ActionButton
                                                    actionType="button"
                                                    variant="outlined"
                                                    disabled={isReadOnly}
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            ...EMPTY_KOSTNADSLINJE,
                                                        })
                                                    }
                                                    translation="prosjekt.kostnadskalkyle.table.addRow"
                                                />
                                            )}
                                        </Block>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </>
                    );
                }}
            </FieldArray>
        </Table>
    );
};
